import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { api } from "../../api";
import {
  Settings,
  AlertTriangle,
  CheckCircle,
  Loader2,
  ChevronRight,
  Layers,
  GripVertical,
  Save,
  ArrowDownUp,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface Tier {
  id: number;
  name: string;
  displayOrder: number;
  minValue: number;
  maxValue: number | null;
  color: string;
}

interface Machine {
  id: number;
  code: string;
  name: string;
  priceUsd: number;
  status: string;
  isPublic: boolean;
  tiersConfigured: boolean;
  displayOrder: number;
  tiers: Tier[];
}

export function AdminDashboardPage() {
  const [machines, setMachines] = useState<Machine[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orderChanged, setOrderChanged] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncingOrder, setIsSyncingOrder] = useState(false);

  async function fetchMachines() {
    try {
      const response = await api.admin.machines.get();
      if (response.data && "data" in response.data) {
        const data = response.data.data as Machine[];
        // Sort by displayOrder
        data.sort((a, b) => a.displayOrder - b.displayOrder);
        setMachines(data);
      }
    } catch (err) {
      setError("Failed to load machines");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchMachines();
  }, []);

  const sensors = useSensors(
    // 5px activation distance keeps clicks on the "Configure" link from
    // accidentally starting a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setMachines((current) => {
      const oldIndex = current.findIndex((m) => m.id === active.id);
      const newIndex = current.findIndex((m) => m.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return current;
      const reordered = arrayMove(current, oldIndex, newIndex);
      return reordered.map((m, i) => ({ ...m, displayOrder: i }));
    });
    setOrderChanged(true);
  };

  const saveOrder = async () => {
    setIsSaving(true);
    try {
      await api.admin.machines.order.put({
        machines: machines.map((m, i) => ({
          id: m.id,
          displayOrder: i,
        })),
      });
      setOrderChanged(false);
    } catch (err) {
      console.error("Failed to save order:", err);
      setError("Failed to save order");
    } finally {
      setIsSaving(false);
    }
  };

  const syncOrderFromApi = async () => {
    setIsSyncingOrder(true);
    setError(null);
    try {
      const response = await api.admin.machines.order.sync.post();
      if (response.data && "error" in response.data) {
        setError(response.data.error as string);
        return;
      }
      // Server order is now canonical; discard any unsaved local reordering
      await fetchMachines();
      setOrderChanged(false);
    } catch (err) {
      console.error("Failed to sync order:", err);
      setError("Failed to sync order from API");
    } finally {
      setIsSyncingOrder(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  const unconfiguredCount = machines.filter((m) => !m.tiersConfigured).length;
  const configuredCount = machines.filter((m) => m.tiersConfigured).length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Admin Dashboard</h1>
        <p className="text-muted-foreground mt-1">
          Manage tier configurations for gacha machines
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Machines
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{machines.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Configured
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-500">
              {configuredCount}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Pending Configuration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-amber-500">
              {unconfiguredCount}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Machines List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5" />
              Gacha Machines
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={syncOrderFromApi}
                disabled={isSyncingOrder || isSaving}
                title="Reorder machines to match the CollectorCrypt API"
              >
                {isSyncingOrder ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ArrowDownUp className="w-4 h-4 mr-2" />
                )}
                Sort by API Order
              </Button>
              {orderChanged && (
                <Button onClick={saveOrder} disabled={isSaving} size="sm">
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Order
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={machines.map((m) => m.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="divide-y divide-border">
                {machines.map((machine) => (
                  <SortableMachineRow key={machine.id} machine={machine} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </CardContent>
      </Card>
    </div>
  );
}

function SortableMachineRow({ machine }: { machine: Machine }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: machine.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between py-4 bg-card"
    >
      <div className="flex items-center gap-4">
        <button
          {...attributes}
          {...listeners}
          className="p-1 cursor-grab active:cursor-grabbing hover:bg-secondary rounded touch-none"
          title="Drag to reorder"
          aria-label={`Reorder ${machine.name}`}
        >
          <GripVertical className="w-4 h-4 text-muted-foreground" />
        </button>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-medium">{machine.name}</h3>
            {machine.tiersConfigured ? (
              <Badge variant="default" className="bg-green-500/20 text-green-400">
                Configured
              </Badge>
            ) : (
              <Badge variant="destructive" className="bg-amber-500/20 text-amber-400">
                Pending
              </Badge>
            )}
            {!machine.isPublic && (
              <Badge
                variant="secondary"
                className="bg-muted text-muted-foreground"
                title="Not listed on CollectorCrypt (third-party / unlisted). Hidden from the public selector."
              >
                Hidden
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
            <span className="font-mono">{machine.code}</span>
            <span>${machine.priceUsd}</span>
            <span className="capitalize">{machine.status}</span>
          </div>
          {machine.tiers.length > 0 && (
            <div className="flex items-center gap-1 mt-2">
              {machine.tiers
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map((tier) => (
                  <span
                    key={tier.id}
                    className="px-2 py-0.5 text-xs rounded"
                    style={{
                      backgroundColor: `${tier.color}20`,
                      color: tier.color,
                    }}
                  >
                    {tier.name}
                  </span>
                ))}
            </div>
          )}
        </div>
      </div>
      <Link to={`/admin/machines/${machine.id}`}>
        <Button variant="ghost" size="sm">
          <Settings className="w-4 h-4 mr-2" />
          Configure
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </Link>
    </div>
  );
}
